import { Box, Flex, Heading, Progress, Text } from "@radix-ui/themes";
import ChartRenderer from "@/components/entity/charts/ChartRenderer";
import FunnelChartView from "@/components/entity/charts/FunnelChartView";
import DeltaBadge from "@/components/ui/DeltaBadge";
import { ChartConfiguration, ChartDataSource, ChartType } from "@/types/breeze/ChartConfiguration";
import { PaginatedResult } from "@/types/breeze/Any";
import { DashboardWidget } from "@/types/breeze/Dashboard";

interface ChartConfigurationWire {
  id: number;
  name: string;
  data_source: ChartDataSource;
  chart_type: ChartType;
  entity_name?: string | null;
  x_field?: string | null;
  y_field?: string | null;
  pie_mode?: ChartConfiguration["pieMode"];
  platform_metric?: string | null;
  platform_period?: string | null;
  quota_metric?: string | null;
  stack_by?: string | null;
  y_agg?: ChartConfiguration["yAgg"];
  x_interval?: ChartConfiguration["xInterval"];
  timeframe_preset?: ChartConfiguration["timeframePreset"];
  timeframe_from?: string | null;
  timeframe_to?: string | null;
  timeframe_offset_days?: number | null;
  compare_enabled?: boolean | null;
  compare_previous_days?: number | null;
  compare_show_overlay?: boolean | null;
  limit?: number | null;
  sort_direction?: ChartConfiguration["sortDirection"];
  cumulative?: boolean | null;
  target?: number | null;
  target_mode?: ChartConfiguration["targetMode"];
  filters?: ChartConfiguration["filters"];
  funnel_stages?: ChartConfiguration["funnelStages"];
  funnel_style?: ChartConfiguration["funnelStyle"];
  series?: string[];
  config?: string | null;
}

interface ChartData {
  chart?: ChartConfigurationWire;
  items?: Record<string, unknown>[];
  total?: number;
  value?: number;
  target?: number;
  label?: string;
  usage?: number;
  quota?: number;
  unit?: string;
  stages?: { label: string; count: number }[];
  error?: string;
  primary?: ChartData;
  compare?: { items?: Record<string, unknown>[] } | null;
  delta_pct?: number | null;
  compare_period_days?: number | null;
}

interface Props {
  widget: DashboardWidget;
  data?: ChartData;
  loading?: boolean;
}

function unwrapCompare(data: ChartData): { data: ChartData; compareItems: Record<string, unknown>[] | null; deltaPct: number | null; periodDays: number | null } {
  if (data.primary) {
    return {
      data: data.primary,
      compareItems: data.compare?.items ?? null,
      deltaPct: data.delta_pct ?? null,
      periodDays: data.compare_period_days ?? null,
    };
  }
  return { data, compareItems: null, deltaPct: null, periodDays: null };
}

function toChartConfiguration(wire: ChartConfigurationWire): ChartConfiguration {
  return {
    id: wire.id,
    name: wire.name,
    dataSource: wire.data_source,
    chartType: wire.chart_type,
    entityName: wire.entity_name,
    xField: wire.x_field,
    yField: wire.y_field,
    pieMode: wire.pie_mode,
    platformMetric: wire.platform_metric,
    platformPeriod: wire.platform_period,
    stackBy: wire.stack_by,
    quotaMetric: wire.quota_metric,
    yAgg: wire.y_agg,
    xInterval: wire.x_interval,
    timeframePreset: wire.timeframe_preset,
    timeframeFrom: wire.timeframe_from,
    timeframeTo: wire.timeframe_to,
    timeframeOffsetDays: wire.timeframe_offset_days,
    compareEnabled: wire.compare_enabled,
    comparePreviousDays: wire.compare_previous_days,
    compareShowOverlay: wire.compare_show_overlay,
    limit: wire.limit,
    sortDirection: wire.sort_direction,
    cumulative: wire.cumulative,
    target: wire.target,
    targetMode: wire.target_mode,
    filters: wire.filters,
    funnelStages: wire.funnel_stages,
    funnelStyle: wire.funnel_style,
    series: wire.series,
    config: wire.config ?? undefined,
  };
}

export function ChartWidget({ widget, data, loading }: Props) {
  const unwrapped = data ? unwrapCompare(data) : null;
  const inner = unwrapped?.data;
  return (
    <Box p="4" style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Text size="1" color="gray" mb="2" asChild>
        <div>{widget.title}</div>
      </Text>
      {loading && <Text size="2" color="gray">Loading…</Text>}
      {!loading && inner?.error && <Text size="2" color="red">{inner.error}</Text>}
      {!loading && !inner?.error && inner?.chart && renderBody(inner, unwrapped!.compareItems, unwrapped!.deltaPct, unwrapped!.periodDays)}
    </Box>
  );
}

function renderBody(data: ChartData, compareItems: Record<string, unknown>[] | null, deltaPct: number | null, periodDays: number | null) {
  const chart = data.chart!;
  if (chart.chart_type === "gauge") return renderGauge(data, deltaPct, periodDays);
  if (chart.chart_type === "stat") return renderStat(data, deltaPct, periodDays);
  if (chart.chart_type === "funnel") return renderFunnel(data);
  return renderChart(data, compareItems, deltaPct, periodDays);
}

function renderFunnel(data: ChartData) {
  return (
    <Box style={{ flex: 1, minHeight: 0 }}>
      <FunnelChartView
        stages={data.stages ?? []}
        style={data.chart?.funnel_style ?? "vertical"}
        height="100%"
        emptyMessage="No stages configured"
      />
    </Box>
  );
}

function renderStat(data: ChartData, deltaPct: number | null, periodDays: number | null) {
  const value = data.value ?? 0;
  return (
    <>
      <Heading size="8" weight="bold" mt="2">
        {value.toLocaleString()}
      </Heading>
      {data.label && (
        <Text size="1" color="gray" mt="1" asChild>
          <div>{data.label}</div>
        </Text>
      )}
      {deltaPct !== null && deltaPct !== undefined && (
        <Box mt="1"><DeltaBadge value={deltaPct} periodDays={periodDays} /></Box>
      )}
    </>
  );
}

function renderGauge(data: ChartData, deltaPct: number | null, periodDays: number | null) {
  const value = data.value ?? data.usage ?? 0;
  const target = data.target ?? data.quota ?? 0;
  const unit = data.unit ?? "";
  const pct = target > 0 ? Math.min(100, (value / target) * 100) : 0;
  const mode = data.chart?.target_mode ?? "warning";
  const color: "green" | "amber" | "red" = mode === "goal"
    ? (pct >= 90 ? "green" : pct >= 60 ? "amber" : "red")
    : (pct >= 90 ? "red" : pct >= 70 ? "amber" : "green");
  return (
    <>
      <Flex align="baseline" gap="2" mb="2">
        <Heading size="6" weight="bold">{formatNumber(value)}</Heading>
        <Text size="2" color="gray">of {formatNumber(target)} {unit}</Text>
      </Flex>
      <Progress value={pct} color={color} size="2" />
      <Flex align="center" gap="2" mt="2">
        <Text size="1" color="gray" asChild>
          <div>{pct.toFixed(1)}%</div>
        </Text>
        {deltaPct !== null && deltaPct !== undefined && <DeltaBadge value={deltaPct} periodDays={periodDays} />}
      </Flex>
    </>
  );
}

function renderChart(data: ChartData, compareItems: Record<string, unknown>[] | null, deltaPct: number | null, periodDays: number | null) {
  return (
    <Box style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      {deltaPct !== null && deltaPct !== undefined && (
        <Box mb="1"><DeltaBadge value={deltaPct} periodDays={periodDays} /></Box>
      )}
      <Box style={{ flex: 1, minHeight: 0 }}>
        <ChartRenderer
          chart={toChartConfiguration(data.chart!)}
          entityData={
            {
              items: data.items ?? [],
              total_items: data.total ?? 0,
              total_pages: 1,
              page: 1,
              page_size: data.items?.length ?? 0,
              has_next_page: false,
              has_previous_page: false,
            } as PaginatedResult<Record<string, unknown>>
          }
          compareItems={compareItems}
          height="100%"
          showNoData
        />
      </Box>
    </Box>
  );
}

function formatNumber(n: number): string {
  if (n >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return n.toFixed(n < 10 && n !== Math.floor(n) ? 2 : 0);
}
